/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * THE REVIEW QUEUE — the Dashboard's derivation (W82 §1, the pro web step 2). PURE; `reviewQueue.test.ts` runs it.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * The coach's landing lists the work in the order it wants doing: TO REVIEW (every finished workout nobody has
 * reviewed, oldest finish first), then NEEDS YOU, then everyone else with the week line. Every piece below is a READ of
 * a reader that already exists — this file adds the queue's facts and words and nothing that another screen decides:
 *
 *   • FINISHED — `rosterData.sessionFinishedAt` (the roster's logged-set rule, ONE reader: `loggedAt`).
 *   • THE WINDOW, THE FOLD, THE ORDER — `reviewQueueOrder.ts`, the twin of the phone's `ReviewQueueOrder.swift`, proven
 *     equal on the Swift-emitted corpus. 14 days by finish from Monday of last week, and it stays 14 days once reviews
 *     exist: a review is there to set next week, and a finish older than that has already been overtaken by a week
 *     nobody reviewed (Fable's ruling, 2026-09-25).
 *   • NEEDS YOU — `thisWeekBoard.needsOf` / `needsYou` (the board's sentences); the ORDER reads two of its kinds.
 *   • A PR — `progressHistory.buildHistories` (the phone's merge twin) and `progressChart.personalRecord` (the chart's
 *     own record rule: the best over the history, FIRST day reached — a tie is not a PR).
 *   • WORKING SETS — `progressHistory.isWorking` + `sectionIncludes` (the phone's `SetLogKind` / `ProgressListFilter`).
 *
 * ⚠️ REVIEWED = a `coach_messages` row with `kind = 'review'` naming the session in `session_id`. Neither column exists
 * until step 4 adds them, so the reader is typed against OPTIONAL fields: today nothing is reviewed and every finished
 * day queues, which the page says in one quiet line (`Finished since Mon 14 Sep`).
 *
 * ⚠️ "WRITTEN" HAS NO HOME ON A SESSION — `workout_session_exercises` carries only what was performed. It is read from
 * the day's ASSIGNED COPY (Fable's ruling Q3): its sets outside warm-up / cool-down sections, not typed `warm_up`. A day
 * holding 0 or 2+ training assignments has no single written count, so it is null and the row says `15 sets`.
 */

import { loggedAt, sessionFinishedAt } from "./rosterData";
import { buildHistories, instanceDate, isWorking, sectionIncludes, type ExerciseHistory, type HistoryInstance } from "./progressHistory";
import { allowed, fieldsFor, personalRecord, resolvedField, sameDay, seriesPoints } from "./progressChart";
import { ENGINE_RULE, type OneRMRule, type WeighIn } from "./oneRepMax";
import { dayMonth, namedNeeds, type BoardRow, type Need } from "./thisWeekBoard";
import { mondayKey } from "./weekLine";
import { dayKeyOf, trainedDayKey } from "./days";
import { clientOrder, codePointCompare, finishedDays, waiting, windowStart, type QueueClient, type QueueRecord } from "./reviewQueueOrder";
import { shortSets, unloggedExercises, type PlanSection } from "./setVerdict";
import { reviewState } from "./reviewState";
import { repliesWaiting, splitTitled, type FoldRow } from "./reviewBody";

/* ── the raw shapes the page's reads hand in ────────────────────────────────────────────────────── */

export type QLog = {
  readonly status: string;
  readonly completed_at: string | null;
  readonly rpe: number | null;
  readonly note: string | null;
  readonly set_number: number | null;
  readonly reps_completed: number | null;
  readonly weight_kg: number | null;
  readonly hold_seconds: number | null;
  /** W125-F2 — read by the exact 1RM only (the chart's own inputs); absent = not read. */
  readonly assist_kg?: number | null;
  readonly modifier_label_override?: string | null;
  /** W136 B2 S3 — a run's distance / a rounds set's rounds (`setVerdict.classify` judges a run by them); absent = not read. */
  readonly distance_m?: number | null;
  readonly rounds?: number | null;
};

/** W125-F2 — one library row's chart settings, as the chart reads them: the coach's ticks and the `1RM` switch. A key with
 *  no entry is an ENGINE exercise (today's chart exactly); the loader seeds every coach key OFF before its read lands. */
export type QChart = { readonly progressViews: readonly string[]; readonly oneRM: OneRMRule };
/** What a PR is judged against beyond the history: the movements' chart settings and the client's weigh-ins. */
export type PrContext = { readonly charts?: Readonly<Record<string, QChart>>; readonly weighIns?: readonly WeighIn[] };

export type QExercise = {
  readonly id: string;
  readonly position: number | null;
  readonly section_role: string | null;
  /** The library id (`coach_exercise_id ?? exercise_id`) — the movement's key, as Progress keys it. */
  readonly key: string | null;
  readonly name: string;
  readonly logs: readonly QLog[];
  readonly clips: number;
};

export type QSession = {
  readonly id: string;
  readonly userId: string;
  readonly kind: string;
  readonly completedAt: string | null;
  readonly scheduledDate: string | null;
  readonly createdAt: string | null;
  readonly exercises: readonly QExercise[];
};

/** One TRAINING assignment of the window: its day, its workout's name, its written sets (`writtenSets`) and — for the
 *  `as written` claim — the copy's tree (`sections`; absent = not read, and nothing is claimed about the numbers). */
export type QAssignment = {
  readonly clientId: string; readonly dayKey: string; readonly workoutName: string | null; readonly written: number;
  readonly sections?: readonly PlanSection[];
};

/** A `coach_messages` row as the review reader sees it (W82 step 4's columns). A row with no `kind` predates them and is a
 *  plain message; `created_at` dates the review (the queue's `Reviewed today` foot). */
export type ReviewMark = { readonly id?: string; readonly kind?: string | null; readonly session_id?: string | null; readonly created_at?: string | null; readonly read_at?: string | null };

/** A `coach_messages` row of a client's conversation, as the replies-waiting rule reads it (W82 §8(6)). */
export type QThreadRow = FoldRow & { readonly clientId: string; readonly fromCoach: boolean; readonly exerciseRowId?: string | null };

/** An exercise thread whose last word is the client's — the queue's second group (his priority: review first, then these). */
export type ReplyRow = {
  readonly commentId: string; readonly clientId: string; readonly firstName: string; readonly exercise: string;
  readonly sessionId: string | null; readonly exerciseRowId: string | null; readonly since: number;
  /** W101 — the workout the thread belongs to (its session's scheduled day's one assignment, the queue row's own rule);
   *  null when the session is outside the read or its day holds no single assignment. */
  readonly workout: string | null;
};

/* ── the pieces ─────────────────────────────────────────────────────────────────────────────────── */

/** The sessions a coach has reviewed: a message typed `review` naming one. Nothing else counts as a review. */
export function reviewedSessionIds(marks: readonly ReviewMark[]): Set<string> {
  const out = new Set<string>();
  for (const m of marks) if (m.kind === "review" && typeof m.session_id === "string" && m.session_id !== "") out.add(m.session_id);
  return out;
}

/** When each reviewed session was reviewed (ms; the first review row naming it). */
export function reviewedAtBySession(marks: readonly ReviewMark[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const m of marks) {
    if (m.kind !== "review" || typeof m.session_id !== "string" || typeof m.created_at !== "string") continue;
    const at = Date.parse(m.created_at);
    if (Number.isFinite(at) && !(out.get(m.session_id)! <= at)) out.set(m.session_id, at);
  }
  return out;
}

/** The sets a coach WROTE for a day, from the assigned copy's tree: outside warm-up / cool-down, not typed warm-up. */
export function writtenSets(sections: readonly { section_type: string | null; exercises: readonly { sets: readonly { set_type: string | null }[] }[] }[]): number {
  let n = 0;
  for (const s of sections) {
    if (s.section_type === "warm_up" || s.section_type === "cool_down") continue;
    for (const e of s.exercises) for (const set of e.sets) if (set.set_type !== "warm_up") n += 1;
  }
  return n;
}

const mainLogs = (s: QSession) =>
  s.exercises.filter((e) => sectionIncludes(e.section_role)).flatMap((e) => e.logs.map((l) => ({ e, l })));

/** A session as the order's record, or null when it is not finished (the roster's rule). */
export function recordOf(s: QSession): QueueRecord | null {
  const finish = sessionFinishedAt(s.completedAt, s.exercises.flatMap((e) => e.logs));
  if (finish === null) return null;
  const at = Date.parse(finish);
  if (!Number.isFinite(at)) return null;
  return { id: s.id, clientId: s.userId, kind: s.kind, finish: at, workingSets: mainLogs(s).filter(({ l }) => isWorking(l.status)).length };
}

export type Flag =
  | { readonly kind: "pr"; readonly exercise: string }
  | { readonly kind: "rpe"; readonly rpe: number; readonly exercise: string }
  | { readonly kind: "skipped"; readonly sets: number }
  | { readonly kind: "notLogged"; readonly sets: number };

/**
 * THE THREE COUNTS OF A DAY (Fable's ruling, 2026-09-26 — the codex's clean / missed / notLogged): DONE = working sets
 * performed; SKIPPED = sets the athlete MARKED skipped (a `skipped` status row); NOT LOGGED = written − done − skipped, the
 * sets that left no row at all. ⚠️ "Skipped" is said only when a row says so: collapsing an untouched set into "skipped"
 * would tell a coach the athlete refused work that may simply not have been recorded. MEASURED on the render fixture: his
 * 23 Sep "Legs & Core" writes 32 working sets (Knee 9 · Hip 11 · Calf & grip 12); the fullest of the day's three training
 * records holds 15 and the other two are restarts of the same opening, so 17 have no row anywhere — `17 not logged`.
 * NOT LOGGED is null when there is no single written count.
 */
export function dayCounts(session: QSession, written: number | null): { done: number; skipped: number; notLogged: number | null } {
  const main = mainLogs(session);
  const done = main.filter(({ l }) => isWorking(l.status)).length;
  const skipped = main.filter(({ l }) => l.status === "skipped").length;
  return { done, skipped, notLogged: written === null ? null : Math.max(0, written - done - skipped) };
}

/** The movement histories of ONE client, built the way Progress builds them (the phone's merge twin). */
export function historiesOf(sessions: readonly QSession[]): ExerciseHistory[] {
  const instances: HistoryInstance[] = [];
  for (const s of sessions) {
    for (const e of s.exercises) {
      if (e.key === null || !sectionIncludes(e.section_role) || e.logs.length === 0) continue;
      const ms = (iso: string | null) => (iso === null ? null : Date.parse(iso));
      const date = instanceDate(e.logs.map((l) => ms(loggedAt(l))), {
        scheduled: ms(s.scheduledDate), completed: ms(s.completedAt), created: ms(s.createdAt),
      });
      if (date === null) continue;
      instances.push({
        key: e.key, name: e.name, instanceId: e.id, sessionId: s.id, date,
        logs: [...e.logs].sort((a, b) => (a.set_number ?? 0) - (b.set_number ?? 0)).map((l) => ({
          setNumber: l.set_number ?? 0, status: l.status, reps: l.reps_completed, weightKg: l.weight_kg, holdSeconds: l.hold_seconds,
          // W125-F2: the exact 1RM's inputs, as the client's chart reads them (`useProgressHistory`).
          rpe: l.rpe, assistKg: l.assist_kg ?? null, band: l.modifier_label_override ?? "",
          // W136 B2 S3r5 P2 (D8): a run's / rounds set's own facts — its time is never a hold, so a slower run is never a PR.
          distanceM: l.distance_m ?? null, rounds: l.rounds ?? null,
        })),
      });
    }
  }
  return buildHistories(instances);
}

/**
 * Whether this session set a record on a movement: its entry survives the day's fold and is the history's record as of
 * that day — the chart's field (`resolvedField` over the whole history), the chart's record rule (`personalRecord`: the
 * best, FIRST day reached), and at least one earlier day to beat. Returns the movement's name, in the session's order.
 *
 * 🔴 W125-F2 (Fable's ruling 2, 2026-10-02): THE FIELD IS THE ONE THE CHART OPENS ON — `allowed` over the coach's ticks and
 * the `1RM` switch, then the first. A switch OFF is judged on Top set (never on an estimate nobody can see); ON, on the
 * exact estimate with the body and the weigh-ins as the chart computes it — no weigh-in, no 1RM point, so no 1RM PR.
 */
export function prIn(session: QSession, histories: readonly ExerciseHistory[], ctx: PrContext = {}): string | null {
  const order = [...session.exercises].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  for (const e of order) {
    for (const h of histories) {
      const entry = h.entries.find((x) => x.sessionId === session.id && x.exerciseInstanceId === e.id);
      if (entry === undefined) continue;
      const chart = ctx.charts?.[h.id];
      const rule: OneRMRule = chart?.oneRM ?? ENGINE_RULE;
      const field = resolvedField(null, allowed(fieldsFor(h.entries, rule), chart === undefined ? null : chart.progressViews, rule));
      if (field === null) continue;
      const points = seriesPoints(field, h.entries.filter((x) => x.date <= entry.date), rule, ctx.weighIns ?? []);
      const record = personalRecord(points);
      if (points.length >= 2 && record !== null && sameDay(record.date, entry.date)) return h.name;
    }
  }
  return null;
}

/**
 * EVERY flag a day earns, in the fixed priority: a PR · an RPE ≥ 9 on main work · sets SKIPPED (status rows) · sets NOT
 * LOGGED (`dayCounts`). The queue row shows the FIRST (`flagOf`); the review's head shows them all (W82 step 3) — one list,
 * so the two screens can never rank a day's news differently.
 */
export function flagsOf(session: QSession, histories: readonly ExerciseHistory[], written: number | null = null,
                        ctx: PrContext = {}): Flag[] {
  const out: Flag[] = [];
  const pr = prIn(session, histories, ctx);
  if (pr !== null) out.push({ kind: "pr", exercise: pr });
  let hard: { rpe: number; exercise: string } | null = null;
  for (const { e, l } of mainLogs(session)) {
    if (!isWorking(l.status) || l.rpe === null || l.rpe < 9) continue;
    if (hard === null || l.rpe > hard.rpe) hard = { rpe: l.rpe, exercise: e.name };
  }
  if (hard !== null) out.push({ kind: "rpe", ...hard });
  const c = dayCounts(session, written);
  if (c.skipped > 0) out.push({ kind: "skipped", sets: c.skipped });
  if (c.notLogged !== null && c.notLogged > 0) out.push({ kind: "notLogged", sets: c.notLogged });
  return out;
}

/** ONE flag by the same priority — the queue row's chip. */
export function flagOf(session: QSession, histories: readonly ExerciseHistory[], written: number | null = null,
                       ctx: PrContext = {}): Flag | null {
  return flagsOf(session, histories, written, ctx)[0] ?? null;
}

/* ── the queue ──────────────────────────────────────────────────────────────────────────────────── */

export type QueueRow = {
  readonly sessionId: string;
  readonly clientId: string;
  readonly name: string;
  readonly firstName: string;
  readonly workout: string | null;
  readonly finish: number;
  readonly done: number;
  readonly written: number | null;
  /** Sets marked skipped (status rows) and sets that left no row (written − done − skipped; null without a written count). */
  readonly skipped: number;
  readonly notLogged: number | null;
  /** Written working sets that ended SHORT of their numbers (`setVerdict.shortSets`); null when the day's copy was not
   *  read or the record does not line up with it. */
  readonly short: number | null;
  readonly notes: number;
  readonly clips: number;
  readonly flag: Flag | null;
  /** Every flag the day earns, priority first (`flagsOf`); `flag` is its head. */
  readonly flags: readonly Flag[];
  readonly reviewed: boolean;
  /** This training DAY (W112: scheduled, else the finish's day) falls in the current week (Monday on) — the watch's "this week". */
  readonly thisWeek: boolean;
  /** Main-section exercises with a `skipped` set ROW that day — the two-week rule's input. ⚠️ Rows only: an untouched
   *  exercise cannot be named without pairing plan to work (the review page's job, step 3), so this is the part of
   *  "skipped" that can say WHICH movement. */
  readonly skippedKeys: readonly { key: string; name: string }[];
  /** Main-section exercises of the day's copy with a written working set that left NO log (`setVerdict.unloggedExercises`)
   *  — the other half of the two-week watch (W82 step 4). Empty when no single copy was read or the record does not line up. */
  readonly notLoggedKeys: readonly { key: string; name: string }[];
  /** When the review of this day was sent (ms), or null. */
  readonly reviewedAt: number | null;
  /** The client has opened the review (`reviewState` = seen, the one derivation §8 names); false while unseen. */
  readonly seen: boolean;
};

export type ReviewQueue = {
  readonly since: number;
  /** Every finished day of the window, reviewed or not, oldest first. */
  readonly days: readonly QueueRow[];
  /** TO REVIEW — the unreviewed days, oldest finish first. */
  readonly waiting: readonly QueueRow[];
  /** Active clients in the queue's order (`clientOrder`). */
  readonly order: readonly BoardRow[];
  readonly needsYou: readonly BoardRow[];
  /** Active clients with nothing waiting and nothing needed, in the order. */
  readonly others: readonly BoardRow[];
  readonly invited: readonly BoardRow[];
  /** REPLIES WAITING (§8(6)) — oldest wait first; every active client's exercise threads whose last row is the client's. */
  readonly replies: readonly ReplyRow[];
  /** W111 — CRITICAL: active clients whose training runs out now (`criticalRows`), soonest last day first. */
  readonly critical: readonly CriticalRow[];
};

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * W111 — ONE `To do` LIST, `critical` FIRST (his word, 2026-09-30 ~18:00: *"what purpose does "needs you" have? since we
 * have the review queue and can mention "critical" or something like that? when it's like 24 hours left to the next workout
 * and there's nothing assigned after that."*; Fable's contract). The Swift twin is `CoachDashboardFacts.criticalRows` /
 * `todo`, pinned equal on the dashboard corpus.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * CRITICAL = an ACTIVE client (accepted, not ended, not merely invited) whose LAST assigned TRAINING day (tasks never count —
 * the roster's `missed` predicate) is today or tomorrow, or passed within the last 7 days. "Today/tomorrow" are the coach's
 * calendar days (`calendarToday`); an assignment day is a bare date, so no instant is compared. A client with no assignment
 * ever, or whose last one is more than 7 days past, is NOT on the Dashboard — the Clients page says it.
 * ⚠️ `Nothing assigned` (no day at all) CANNOT OCCUR: a row qualifies only through a last day inside the window. */
export const CRITICAL_PAST_DAYS = 7;
export type CriticalRow = { readonly clientId: string; readonly name: string; readonly firstName: string; readonly lastDay: string };

const addDays = (key: string, n: number) => new Date(Date.parse(`${key}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

export function criticalRows(rows: readonly BoardRow[], todayKey: string): CriticalRow[] {
  const from = addDays(todayKey, -CRITICAL_PAST_DAYS), to = addDays(todayKey, 1);
  return rows
    .filter((r) => r.status === "active" && !r.invited && r.clientId !== null)
    .flatMap((r) => {
      const last = r.lastAssignedISO ?? null;
      return last !== null && last >= from && last <= to
        ? [{ clientId: r.clientId as string, name: r.name, firstName: r.firstName, lastDay: last }] : [];
    })
    .sort((a, b) => (a.lastDay !== b.lastDay ? (a.lastDay < b.lastDay ? -1 : 1)
      : codePointCompare(a.name, b.name) !== 0 ? codePointCompare(a.name, b.name) : (a.clientId < b.clientId ? -1 : a.clientId > b.clientId ? 1 : 0)));
}

/**
 * The row's PLAIN words — WILLIAM'S OWN (his first look, 2026-10-14: *"What does critical mean? Maybe you could write 'overdue x
 * days'"*; Ioannis 10-15: *"as william said, you need to say: overdue x days"*): `Overdue 3 days` once the last assigned day has
 * passed; before that, the same shape with the state in front — `Last day today` · `Last day tomorrow` (Claude's call on his
 * question, 10-15). `today` is the coach's calendar day (`calendarToday`). The Swift twin is `criticalWords(_:today:)`, pinned
 * on the dashboard corpus.
 */
export function criticalWords(lastDay: string, today: string): string {
  if (lastDay >= today) return `Last day ${lastDay === today ? "today" : "tomorrow"}`;
  const ago = Math.round((Date.parse(`${today}T00:00:00Z`) - Date.parse(`${lastDay}T00:00:00Z`)) / 86_400_000);
  return `Overdue ${plural(ago, "day")}`;
}

/** A reply row's second line in the ONE list: `Reply · <workout>`, the exercise's name when no workout is known (a reply
 *  must say it is one beside the reviews). The Swift twin is `ReplyRow.subject`, pinned on the corpus. */
export function replySubject(r: Pick<ReplyRow, "workout" | "exercise">): string {
  return `Reply · ${r.workout ?? r.exercise}`;
}

export type TodoRow =
  | { readonly kind: "critical"; readonly row: CriticalRow }
  | { readonly kind: "review"; readonly row: QueueRow }
  | { readonly kind: "reply"; readonly row: ReplyRow };

/**
 * THE ONE LIST: critical first (soonest last day first, ties by name), then workouts to review and replies TOGETHER, oldest
 * wait first (a review waits from its finish, a reply from the client's answer); a tie goes to the review, then the id.
 */
export function todoList(q: Pick<ReviewQueue, "critical" | "waiting" | "replies">): TodoRow[] {
  const waits: TodoRow[] = [
    ...q.waiting.map((row): TodoRow => ({ kind: "review", row })),
    ...q.replies.map((row): TodoRow => ({ kind: "reply", row })),
  ];
  const at = (t: TodoRow) => (t.kind === "review" ? t.row.finish : t.kind === "reply" ? t.row.since : 0);
  const id = (t: TodoRow) => (t.kind === "review" ? t.row.sessionId : t.kind === "reply" ? t.row.commentId : "");
  waits.sort((a, b) => at(a) - at(b) || (a.kind === b.kind ? 0 : a.kind === "review" ? -1 : 1) || (id(a) < id(b) ? -1 : id(a) > id(b) ? 1 : 0));
  return [...q.critical.map((row): TodoRow => ({ kind: "critical", row })), ...waits];
}

/** Whether a client needs the coach as the ORDER reads it — `needsOf`'s missed and nothing-after-Sunday kinds. */
export function ordersAsNeedsYou(needs: readonly Need[]): boolean {
  return needs.some((n) => n.kind === "missed" || n.kind === "nothingAfterSunday");
}

export function reviewQueue(input: {
  readonly rows: readonly BoardRow[];
  readonly sessions: readonly QSession[];
  readonly assignments: readonly QAssignment[];
  readonly marks: readonly ReviewMark[];
  /** Movement names by library id, for exercises the record never touched (the plan's own ids). Optional. */
  readonly names?: Readonly<Record<string, string>>;
  /** Every row of the active clients' conversations (the replies-waiting rule's input). Optional: absent = none. */
  readonly threads?: readonly QThreadRow[];
  /** W125-F2: the movements' chart settings by key (absent = every movement an engine one) and each client's weigh-ins. */
  readonly charts?: Readonly<Record<string, QChart>>;
  readonly weighIns?: Readonly<Record<string, readonly WeighIn[]>>;
  readonly now: number;
  /** The calendar day the page is about (`calendarToday()`), for Monday. */
  readonly today: Date;
}): ReviewQueue {
  const active = input.rows.filter((r) => !r.invited && r.clientId !== null);
  const byClient = new Map(active.map((r) => [r.clientId as string, r]));
  const since = windowStart(input.now);
  const reviewed = reviewedSessionIds(input.marks);
  const reviewedAt = reviewedAtBySession(input.marks);
  const stateMarks = input.marks.flatMap((m, i) => (typeof m.created_at === "string" ? [{
    id: m.id ?? String(i), kind: m.kind ?? "message", sessionId: m.session_id ?? null, createdAtISO: m.created_at, readAtISO: m.read_at ?? null,
  }] : []));
  const sessionById = new Map(input.sessions.map((s) => [s.id, s]));

  const records = input.sessions
    .filter((s) => byClient.has(s.userId))
    .map(recordOf)
    .filter((r): r is QueueRecord => r !== null);
  const days = finishedDays(records, since);

  const historyByClient = new Map<string, ExerciseHistory[]>();
  const history = (clientId: string) => {
    let h = historyByClient.get(clientId);
    if (h === undefined) { h = historiesOf(input.sessions.filter((s) => s.userId === clientId)); historyByClient.set(clientId, h); }
    return h;
  };
  const monday = mondayKey(input.today, 0);
  // W101 — a reply row names its workout the way a queue row does: the session's scheduled day, that day's ONE assignment.
  const replyWorkout = (sessionId: string | null): string | null => {
    const s = sessionId === null ? undefined : sessionById.get(sessionId);
    if (s === undefined || s.scheduledDate === null) return null;
    const day = s.scheduledDate.slice(0, 10);
    const onDay = input.assignments.filter((a) => a.clientId === s.userId && a.dayKey === day);
    return onDay.length === 1 ? (onDay[0]?.workoutName ?? null) : null;
  };

  const rowOf = (r: QueueRecord): QueueRow => {
    const s = sessionById.get(r.id) as QSession;
    const client = byClient.get(r.clientId) as BoardRow;
    // W112 — the row's DAY is the training day's rule (`trainedDayKey`): the scheduled day, else the finish in the coach's zone.
    const day = trainedDayKey(s.scheduledDate, new Date(r.finish).toISOString()) ?? "";
    const onDay = input.assignments.filter((a) => a.clientId === r.clientId && a.dayKey === day);
    const main = mainLogs(s);
    const skippedKeys = new Map<string, string>();
    for (const { e, l } of main) if (l.status === "skipped" && e.key !== null) skippedKeys.set(e.key, e.name);
    const written = onDay.length === 1 ? (onDay[0]?.written ?? null) : null;
    const counts = dayCounts(s, written);
    const flags = flagsOf(s, history(r.clientId), written, { charts: input.charts, weighIns: input.weighIns?.[r.clientId] ?? [] });
    const tree = onDay.length === 1 ? onDay[0]?.sections : undefined;
    const short = tree === undefined ? null : shortSets(tree, s.exercises);
    const nameOf = (key: string) => input.names?.[key] ?? s.exercises.find((e) => e.key === key)?.name ?? "an exercise";
    const unlogged = tree === undefined ? [] : (unloggedExercises(tree, s.exercises) ?? []);
    return {
      sessionId: r.id, clientId: r.clientId, name: client.name, firstName: client.firstName,
      workout: onDay.length === 1 ? (onDay[0]?.workoutName ?? null) : null,
      finish: r.finish,
      done: r.workingSets,
      written,
      skipped: counts.skipped,
      notLogged: counts.notLogged,
      short,
      notes: s.exercises.flatMap((e) => e.logs).filter((l) => typeof l.note === "string" && l.note.trim() !== "").length,
      clips: s.exercises.reduce((n, e) => n + e.clips, 0),
      flag: flags[0] ?? null,
      flags,
      reviewed: reviewed.has(r.id),
      thisWeek: day >= monday,
      skippedKeys: [...skippedKeys].map(([key, name]) => ({ key, name })),
      notLoggedKeys: unlogged.map((key) => ({ key, name: nameOf(key) })),
      reviewedAt: reviewedAt.get(r.id) ?? null,
      seen: reviewState(r.id, stateMarks) === "seen",
    };
  };
  const all = days.map(rowOf);
  const waitingIds = new Set(waiting(days, reviewed).map((r) => r.id));
  const waitingRows = all.filter((r) => waitingIds.has(r.sessionId));

  const clients: QueueClient[] = active.map((r) => ({
    id: r.clientId as string, name: r.name, needsYou: ordersAsNeedsYou(r.needs), lastTrainedDay: r.lastTrainedISO,
  }));
  const replies: ReplyRow[] = repliesWaiting((input.threads ?? []).filter((m) => byClient.has(m.clientId)))
    .map(({ comment, sinceISO }) => ({
      commentId: comment.id, clientId: comment.clientId, firstName: (byClient.get(comment.clientId) as BoardRow).firstName,
      exercise: splitTitled(comment.body).head, sessionId: comment.sessionId ?? null, exerciseRowId: comment.exerciseRowId ?? null,
      since: Date.parse(sinceISO), workout: replyWorkout(comment.sessionId ?? null),
    }));
  const order = clientOrder(clients, waiting(days, reviewed), replies.map((r) => ({ clientId: r.clientId, since: r.since })))
    .map((id) => byClient.get(id) as BoardRow);
  const waitingClients = new Set(waitingRows.map((r) => r.clientId));
  return {
    since,
    critical: criticalRows(input.rows, dayKeyOf(input.today)),
    days: all,
    waiting: waitingRows,
    order,
    needsYou: order.filter((r) => ordersAsNeedsYou(r.needs)),
    others: order.filter((r) => !ordersAsNeedsYou(r.needs) && !waitingClients.has(r.clientId as string)),
    invited: input.rows.filter((r) => r.invited && !r.archived),
    replies,
  };
}

/** A NEEDS YOU line, the way the board's day line says it: `Yiannis has nothing after Sunday` (`namedNeeds`, one reader). */
export function needsSentence(r: BoardRow): { name: string; rest: string } {
  const line = namedNeeds([r])[0] ?? r.firstName;
  return { name: r.firstName, rest: line.slice(r.firstName.length).trim() };
}

/** The sidebar's Dashboard count: the workouts waiting AND the exercise threads waiting on a reply (§8(6): the badge
 *  counts both; the page's tile counts workouts and says the replies on its second line). ONE reader. */
export function toReviewCount(q: ReviewQueue): number {
  return q.waiting.length + q.replies.length;
}

/** A reply row's words: `Back Squat · replied 2 days ago` (the wait in the queue's grammar). */
export function replyLine(r: Pick<ReplyRow, "exercise" | "since">, now: number): string {
  const d = waitDays(r.since, now);
  return `${r.exercise} · ${d === 0 ? "replied today" : `replied ${plural(d, "day")} ago`}`;
}

/* ── the words ──────────────────────────────────────────────────────────────────────────────────── */

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_MS = 86_400_000;
const plural = (n: number, one: string, many = `${one}s`) => `${String(n)} ${n === 1 ? one : many}`;

/** Whole calendar days between a finish and now, in the page's zone (0 = finished today). */
export function waitDays(finish: number, now: number): number {
  const a = new Date(finish); a.setHours(0, 0, 0, 0);
  const b = new Date(now); b.setHours(0, 0, 0, 0);
  return Math.max(0, Math.round((b.getTime() - a.getTime()) / DAY_MS));
}

/**
 * A wait turns amber (`--warn`) only at a WEEK (Fable's judgement of the step-2 stills, 2026-09-26): painted on every row,
 * six rows carried six amber phrases and the colour meant nothing. Oldest-first order already says the rest.
 */
export const LONG_WAIT_DAYS = 7;
export function isLongWait(days: number): boolean {
  return days >= LONG_WAIT_DAYS;
}

/**
 * THE QUEUE SHOWS ITS FIRST EIGHT, then `Show all 40 ›` (the same judgement: forty rows is a wall). The choice lives in the
 * URL (`#/?queue=all`) so a link — and a still — can pin it; the tile and the head always count all of them.
 */
export const QUEUE_PAGE = 8;
export function queueShowsAll(query: Readonly<Record<string, string>>): boolean {
  return query.queue === "all";
}
export function visibleWaiting<T>(rows: readonly T[], showAll: boolean): T[] {
  return showAll ? [...rows] : rows.slice(0, QUEUE_PAGE);
}
/** `Show all 40 ›` — only when more wait than the page shows, and only while collapsed. */
export function showAllLabel(total: number, showAll: boolean): string | null {
  return !showAll && total > QUEUE_PAGE ? `Show all ${String(total)} ›` : null;
}

/** W101 — the Dashboard's wait, as short as it can be said: `today` · `1 day` · `3 days`. */
export function waitShort(days: number): string {
  return days === 0 ? "today" : plural(days, "day");
}

/** The empty `To do` card's ONE sentence (job 3 step 5 page 7, 2026-10-09): what the card says and what lands in it. The
 *  phone's empty list says `Nothing waiting` too. */
export const TODO_EMPTY = "Nothing waiting. Finished workouts appear here.";

/**
 * W101 (his word, 2026-09-30: *"As clean as possible! And only the mandatory info!!"*) — the Dashboard's ONE summary line:
 * `3 to review · 2 replies`, each half only when it is not zero, `Nothing waiting` when both are. The two halves sum to the
 * sidebar's badge (`toReviewCount`), so the page and the badge cannot disagree. ⚠️ The WEB no longer draws it (job 3 step 5
 * page 7: it repeated the rows under it and the badge beside it); it stays the phone's twin, pinned on the shared corpus.
 */
export function dashboardSummary(q: Pick<ReviewQueue, "waiting" | "replies"> & { readonly critical?: readonly unknown[] }): string {
  const critical = q.critical?.length ?? 0;
  const parts = [
    // W111: `1 to assign · 6 to review · 2 replies` — the training-ran-out count leads (`to assign`, William's round 10-15:
    // the word `critical` went); it is NOT part of the badge (such a row is not a review), so the badge stays `toReviewCount`.
    critical > 0 ? `${String(critical)} to assign` : null,
    q.waiting.length > 0 ? `${String(q.waiting.length)} to review` : null,
    q.replies.length > 0 ? plural(q.replies.length, "reply", "replies") : null,
  ].filter((p): p is string => p !== null);
  return parts.length === 0 ? "Nothing waiting" : parts.join(" · ");
}

/** `today` · `1 day waiting` · `3 days waiting`. */
export function waitWords(days: number): string {
  return days === 0 ? "today" : `${plural(days, "day")} waiting`;
}

/** `finished Wed 23 Sep 19:10`, in the page's zone. */
export function finishedWords(finish: number): string {
  const d = new Date(finish);
  const p = (n: number) => String(n).padStart(2, "0");
  const key = `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return `finished ${WEEKDAY[d.getDay()] ?? ""} ${dayMonth(key)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * The row's SECOND line: `Pull Skill · Push Strength B · finished Tue 15 Sep 18:33` (the wait follows it, in its own span).
 * ⚠️ THE CLIENT IS ON LINE ONE, ALONE: his workout names carry the middle dot themselves, so `Nikos · Pull Skill · Push
 * Strength B` hid the seam between who and what (Fable, 2026-09-26).
 */
export function whenLine(r: Pick<QueueRow, "workout" | "finish">): string {
  return r.workout === null ? finishedWords(r.finish) : `${r.workout} · ${finishedWords(r.finish)}`;
}

/** `Dana Sample · Upper A` — the row's ACCESSIBLE name (its Review link's label); the page draws the two apart. */
export function rowTitle(r: Pick<QueueRow, "name" | "workout">): string {
  return r.workout === null ? r.name : `${r.name} · ${r.workout}`;
}

/**
 * `18 of 18 sets · 2 notes · 1 clip`; `15 of 32 sets · 17 not logged`; `14 of 16 sets · 2 skipped`; `15 sets` when nothing
 * single was written. Skipped and not-logged each only when non-zero, and never merged.
 * ⚠️ THE CLAUSE THE ROW'S CHIP ALREADY SAYS IS LEFT OUT HERE — rendered, `26 of 27 sets · 1 skipped` stood beside a
 * `1 set skipped` chip; the shortest text never repeats its neighbour (his 2026-09-11 rule).
 */
export function factsLine(r: Pick<QueueRow, "done" | "written" | "skipped" | "notLogged" | "notes" | "clips" | "flag">): string {
  const parts = [r.written === null ? plural(r.done, "set") : `${String(r.done)} of ${plural(r.written, "set")}`];
  if (r.skipped > 0 && r.flag?.kind !== "skipped") parts.push(`${String(r.skipped)} skipped`);
  if (r.notLogged !== null && r.notLogged > 0 && r.flag?.kind !== "notLogged") parts.push(`${String(r.notLogged)} not logged`);
  if (r.notes > 0) parts.push(plural(r.notes, "note"));
  if (r.clips > 0) parts.push(plural(r.clips, "clip"));
  return parts.join(" · ");
}

function rpeText(rpe: number): string {
  return Number.isInteger(rpe) ? String(rpe) : rpe.toFixed(1);
}

/** A flag's words and its MEANING (amendment (d): ok = done / good news, warn = needs you) — the queue's chip and the
 *  review's head say a flag in these words and no others. */
export function flagWords(f: Flag): { text: string; tone: "ok" | "warn" } {
  switch (f.kind) {
    case "pr": return { text: `PR on ${f.exercise}`, tone: "ok" };
    case "rpe": return { text: `RPE ${rpeText(f.rpe)} on ${f.exercise}`, tone: "warn" };
    case "skipped": return { text: `${plural(f.sets, "set")} skipped`, tone: "warn" };
    case "notLogged": return { text: `${plural(f.sets, "set")} not logged`, tone: "warn" };
  }
}

export type QChip = { readonly text: string; readonly tone: "ok" | "warn" | "plain" };

/**
 * THE DAY'S COMPLETION CLAIM — two words, derived, never one claim for two facts (Fable's ruling, 2026-09-26: the queue said
 * `6 of 6 · as written` beside a set two reps short). `as written` ONLY when every written set was done and none fell short
 * of its numbers; `all sets done` when every set was done but at least one fell short (or the numbers cannot be compared —
 * no copy read, or the record does not line up); null otherwise — a set skipped or not logged, or no single written count,
 * and the counts speak. The queue's chip and the review's head both say it through this function.
 */
export function completionWords(r: Pick<QueueRow, "done" | "written" | "skipped" | "notLogged" | "short">): QChip | null {
  if (r.written === null || r.done < r.written || r.skipped > 0 || (r.notLogged ?? 0) > 0) return null;
  return r.short === 0 ? { text: "as written", tone: "ok" } : { text: "all sets done", tone: "plain" };
}

/** The chip: the day's flag, else its completion claim. Null = no chip. */
export function flagChip(r: Pick<QueueRow, "flag" | "done" | "written" | "skipped" | "notLogged" | "short">): QChip | null {
  if (r.flag !== null) return flagWords(r.flag);
  return completionWords(r);
}

/** The queue card's head: `3 waiting · oldest 2 days`, `1 waiting · finished today`, or `nothing waiting`. */
export function waitingLine(rows: readonly QueueRow[], now: number): string {
  const first = rows[0];
  if (first === undefined) return "nothing waiting";
  const d = waitDays(first.finish, now);
  return `${String(rows.length)} waiting · ${d === 0 ? "finished today" : `oldest ${plural(d, "day")}`}`;
}

/** The quiet line under the queue: `Finished since Mon 14 Sep`, and `· none reviewed yet` once reviews can exist. */
export function windowLine(since: number, reviewsExist: boolean, reviewedCount: number): string {
  const d = new Date(since);
  const p = (n: number) => String(n).padStart(2, "0");
  const key = `${String(d.getFullYear())}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  const head = `Finished since ${WEEKDAY[d.getDay()] ?? ""} ${dayMonth(key)}`;
  return reviewsExist && reviewedCount === 0 ? `${head} · none reviewed yet` : head;
}

/** The empty queue, in a human voice. */
export const QUEUE_EMPTY = "Nothing to review — every workout since last Monday has had your eye, or nobody has finished one yet.";

/* ── the four tiles ─────────────────────────────────────────────────────────────────────────────── */

export type Tile = { readonly label: string; readonly value: string; readonly unit: string; readonly detail: string };

/** TO REVIEW: `6` workouts · `oldest 2 days · Dana`. */
export function toReviewTile(q: ReviewQueue, now: number): Tile {
  const n = q.waiting.length;
  const first = q.waiting[0];
  const detail = first === undefined ? "nothing waiting"
    : waitDays(first.finish, now) === 0 ? `oldest finished today · ${first.firstName}`
    : `oldest ${plural(waitDays(first.finish, now), "day")} · ${first.firstName}`;
  const replies = q.replies.length === 0 ? "" : ` · ${plural(q.replies.length, "reply", "replies")} waiting`;
  return { label: "To review", value: String(n), unit: n === 1 ? "workout" : "workouts", detail: detail + replies };
}

/** TRAINED THIS WEEK: `3` of 4 · `clients · 21 – 27 Sep` — the board's own count (`trainedThisWeek`). */
export function trainedTile(counts: { trained: number; active: number }, week: string): Tile {
  return { label: "Trained this week", value: String(counts.trained), unit: `of ${String(counts.active)}`, detail: `clients · ${week}` };
}

/** NEED YOU: `2` clients · `William missed Wednesday · Yiannis has nothing after Sunday` (two, then `and N more`). */
export function needYouTile(q: ReviewQueue, named: (r: BoardRow) => string): Tile {
  const n = q.needsYou.length;
  const shown = q.needsYou.slice(0, 2).map(named);
  if (n > 2) shown.push(`and ${String(n - 2)} more`);
  return { label: "Need you", value: String(n), unit: n === 1 ? "client" : "clients", detail: n === 0 ? "nobody needs you" : shown.join(" · ") };
}

/** NEXT WEEK: `3` of 6 set · `7 changes proposed · 3 still to set` (each half when non-zero). */
export function nextWeekTile(set: number, active: number, changes: number): Tile {
  const parts: string[] = [];
  if (changes > 0) parts.push(`${plural(changes, "change")} proposed`);
  const left = active - set;
  parts.push(left > 0 ? `${String(left)} still to set` : "every client set");
  return { label: "Next week", value: String(set), unit: `of ${String(active)} set`, detail: parts.join(" · ") };
}

/* ── this week at a glance ──────────────────────────────────────────────────────────────────────── */

export type GlanceLine = { readonly text: string; readonly aside: string; readonly tone: "ok" | "warn" };

/** `4 on track · 1 missed a day · 2 invited` — the week line's own states (missed = a `missed` cell). */
export function onTrackLine(rows: readonly BoardRow[]): GlanceLine {
  const active = rows.filter((r) => !r.invited);
  const missed = active.filter((r) => (r.cells ?? []).some((c) => c.state === "missed")).length;
  const invited = rows.filter((r) => r.invited && !r.archived).length;   // N2g — an archived row is not an invitation
  const parts = [`${String(active.length - missed)} on track`];
  if (missed > 0) parts.push(`${String(missed)} missed a day`);
  if (invited > 0) parts.push(`${String(invited)} invited`);
  return { text: parts.join(" · "), aside: plural(active.length, "client"), tone: missed > 0 ? "warn" : "ok" };
}

/** `2 clients set a PR this week` · `Dana · Yiannis` — from every finished day this week, reviewed or not. */
export function prLine(q: ReviewQueue): GlanceLine {
  const names: string[] = [];
  for (const r of q.days) if (r.thisWeek && r.flag?.kind === "pr" && !names.includes(r.firstName)) names.push(r.firstName);
  if (names.length === 0) return { text: "No PRs yet this week", aside: "", tone: "ok" };
  return { text: `${plural(names.length, "client")} set a PR this week`, aside: names.join(" · "), tone: "ok" };
}

/**
 * 🔴 THE TWO-WEEK WATCH, ONCE (W101b, Fable's ruling): the TWO-WEEK RULE (his coaching's own, `COACHING-LOG.md`: a thing
 * seen once is noise, twice running is a watch) — a movement a client SKIPPED sets of in a finished day THIS week AND in one
 * LAST week; and its other half (W82 step 4, from his own logs), a movement whose sets were left UNLOGGED this week and last.
 * ⚠️ "Unlogged" is what the record can say; whether they were DONE it cannot. A movement the skip half names is not said
 * twice. Every item, in ONE list (skips first, then unlogged, each in the days' order): `watchByClient` groups it for the
 * Clients row's `Watch N` and the client page's block (W126); `repeatedSkips` / `repeatedUnlogged` are its two halves in the old sentences (the corpus's glance).
 * The Swift twin is `CoachDashboardFacts.watchItems`, pinned on the corpus.
 */
export type WatchItem = { readonly clientId: string; readonly firstName: string; readonly key: string; readonly name: string; readonly kind: "skipped" | "unlogged" };

export function watchItems(q: Pick<ReviewQueue, "days">): WatchItem[] {
  const out: WatchItem[] = [];
  const thisWeek = q.days.filter((r) => r.thisWeek);
  const twice = (clientId: string, key: string, of: (r: QueueRow) => readonly { key: string }[]) =>
    q.days.some((r) => !r.thisWeek && r.clientId === clientId && of(r).some((x) => x.key === key));
  const said = new Set<string>();
  for (const now of thisWeek) {
    for (const k of now.skippedKeys) {
      const id = `${now.clientId}|${k.key}`;
      if (said.has(id) || !twice(now.clientId, k.key, (r) => r.skippedKeys)) continue;
      said.add(id);
      out.push({ clientId: now.clientId, firstName: now.firstName, key: k.key, name: k.name, kind: "skipped" });
    }
  }
  // Never said twice: a movement SKIPPED this week (whether or not it made the skip half) is not also called unlogged.
  for (const now of thisWeek) for (const k of now.skippedKeys) said.add(`${now.clientId}|${k.key}`);
  for (const now of thisWeek) {
    for (const k of now.notLoggedKeys) {
      const id = `${now.clientId}|${k.key}`;
      if (said.has(id) || !twice(now.clientId, k.key, (r) => r.notLoggedKeys)) continue;
      said.add(id);
      out.push({ clientId: now.clientId, firstName: now.firstName, key: k.key, name: k.name, kind: "unlogged" });
    }
  }
  return out;
}

/** The watch per client, in the list's order (skips first). */
export function watchByClient(q: Pick<ReviewQueue, "days">): Map<string, WatchItem[]> {
  const m = new Map<string, WatchItem[]>();
  for (const w of watchItems(q)) m.set(w.clientId, [...(m.get(w.clientId) ?? []), w]);
  return m;
}

/** ONE watched movement, as the client page's `Watch` block says it: `Pull-ups skipped 2 weeks` / `Pallof Press unlogged
 *  2 weeks`. W126 (his word, 2026-10-01): the Clients row no longer carries a sentence — it carries `Watch N` (the count of
 *  these) and the client's page lists every one in these words; the row's old `watchLine` (first item + ` +N`) went with it. */
export function watchItemLine(w: WatchItem): string {
  return `${w.name} ${w.kind} 2 weeks`;
}

/** `Nikos skipped Pull-ups two weeks running` — the watch's skip half, as the glance said it. */
export function repeatedSkips(q: ReviewQueue): GlanceLine[] {
  return watchItems(q).filter((w) => w.kind === "skipped")
    .map((w) => ({ text: `${w.firstName} skipped ${w.name} two weeks running`, aside: "watch", tone: "warn" as const }));
}

/** `Yiannis left sets of Pallof Press unlogged two weeks running` — the unlogged half, with its logged-vs-done bound. */
export function repeatedUnlogged(q: ReviewQueue): GlanceLine[] {
  return watchItems(q).filter((w) => w.kind === "unlogged")
    .map((w) => ({ text: `${w.firstName} left sets of ${w.name} unlogged two weeks running`, aside: "not logged, maybe done", tone: "warn" as const }));
}

/** The queue's foot once reviews exist: `Reviewed today · Dana (seen) · Alex` (first names in the order reviewed, `(seen)`
 *  once the client opened it — §8); null when nothing was reviewed today (in the page's zone). Not a tab — a line. */
export function reviewedTodayLine(days: readonly QueueRow[], now: number): string | null {
  const start = new Date(now); start.setHours(0, 0, 0, 0);
  const today = days.filter((r) => r.reviewedAt !== null && r.reviewedAt >= start.getTime())
    .sort((a, b) => (a.reviewedAt ?? 0) - (b.reviewedAt ?? 0));
  const names: string[] = [];
  for (const r of today) { const n = r.seen ? `${r.firstName} (seen)` : r.firstName; if (!names.includes(n)) names.push(n); }
  return names.length === 0 ? null : `Reviewed today · ${names.join(" · ")}`;
}

/** The page's day line: `Friday 25 Sep · 6 workouts to review · 2 need you`; `nothing to review` at zero, never `0 workouts`. */
export function queueDayLine(date: string, q: ReviewQueue): string {
  const parts = [date, q.waiting.length === 0 ? "nothing to review" : `${plural(q.waiting.length, "workout")} to review`];
  if (q.needsYou.length > 0) parts.push(`${String(q.needsYou.length)} need you`);
  return parts.join(" · ");
}
